import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowRight, MessageCircle, ShieldCheck } from "lucide-react";
import { useSiteImage } from "@/hooks/useSiteImage";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { trackWhatsAppClick } from "@/lib/gtag";
import AnimatedCounter from "@/components/cinematic/AnimatedCounter";

const heroImageFallback = "/hero-marrakech.webp";

const Hero = () => {
  const { t } = useTranslation();
  const heroImage = useSiteImage("hero", heroImageFallback);

  return (
    <section className="relative min-h-screen flex items-end md:items-center justify-center overflow-hidden section-dark">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt={t("hero.imageAlt")}
          width={1920}
          height={1080}
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className="w-full h-full object-cover object-[58%_center] md:object-center opacity-60"
        />
        {/* Aurora overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-surface-deep/85 via-surface-deep/55 to-surface-deep" />
        <div className="absolute inset-0 bg-aurora aurora-animate opacity-90" aria-hidden />
        {/* Soft top vignette for navbar legibility */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-surface-deep/80 to-transparent" />
      </div>

      <div className="relative z-10 container mx-auto px-6 pt-32 pb-20 md:pt-28 md:pb-24">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full glass-dark px-4 py-1.5 text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-white/90 mb-6 animate-fade-in-up">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            <span className="text-accent">{t("hero.subtitle")}</span>
          </div>

          <h1 className="text-display text-white text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[5.25rem] animate-fade-in-up" style={{ animationDelay: "120ms" }}>
            <span className="md:hidden">{t("hero.titleMobile")}</span>
            <span className="hidden md:block">{t("hero.titleLine1")}</span>
            <span className="hidden md:block">
              <span className="bg-gradient-to-r from-accent via-emerald-300 to-accent bg-clip-text text-transparent">
                {t("hero.titleLine2")}
              </span>
            </span>
          </h1>

          <p className="font-body mt-6 md:mt-8 text-base md:text-xl text-white/85 max-w-2xl leading-relaxed animate-fade-in-up" style={{ animationDelay: "240ms" }}>
            {t("hero.description")}
          </p>

          {/* Glass metric pills */}
          <div className="hidden md:flex mt-10 flex-wrap items-center gap-3 animate-fade-in-up" style={{ animationDelay: "360ms" }}>
            <div className="glass-dark rounded-2xl px-5 py-3">
              <span className="text-accent text-xl font-heading font-extrabold">20+</span>
              <span className="ml-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">{t("hero.citiesLabel")}</span>
            </div>
            <div className="glass-dark rounded-2xl px-5 py-3">
              <span className="text-accent text-xl font-heading font-extrabold">4.9★</span>
              <span className="ml-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">{t("hero.ratingLabel")}</span>
            </div>
            <div className="rounded-2xl px-5 py-3 bg-accent text-accent-foreground ring-accent-soft">
              <span className="text-xl font-heading font-extrabold">✈</span>
              <span className="ml-3 text-[11px] font-semibold uppercase tracking-[0.18em]">{t("hero.vipLabel")}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-8 md:mt-10 animate-fade-in-up" style={{ animationDelay: "480ms" }}>
            <Button
              asChild
              size="lg"
              onClick={() => trackWhatsAppClick('hero_cta')}
              className="w-full sm:w-auto bg-[#25D366] hover:bg-[#1ebe5a] text-white font-body font-semibold text-base md:text-lg px-7 py-6 rounded-full shadow-xl glow-accent"
            >
              <a href={buildWhatsAppUrl()} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 w-5 h-5" />
                {t("hero.ctaWhatsapp")}
              </a>
            </Button>
            <Button asChild size="lg" className="bg-accent text-accent-foreground font-body font-bold text-base md:text-lg px-7 py-6 rounded-full hover:bg-accent/90 shadow-xl glow-accent">
              <a href="#booking">
                {t("hero.cta")}
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="hidden md:inline-flex border-white/40 bg-white/5 text-white font-body text-base md:text-lg px-7 py-6 rounded-full hover:bg-white/15 backdrop-blur-md">
              <a href="#fleet">{t("hero.ctaFleet")}</a>
            </Button>
          </div>

          {/* Trust strip */}
          <div className="mt-10 md:mt-14 flex flex-wrap items-center gap-5 md:gap-10 animate-fade-in-up" style={{ animationDelay: "600ms" }}>
            <div className="flex items-baseline gap-2 text-white">
              <AnimatedCounter value={500} suffix="+" className="text-accent text-3xl md:text-4xl font-heading font-extrabold" />
              <span className="text-sm text-white/70 font-body">{t("hero.clients")}</span>
            </div>
            <div className="w-px h-8 bg-white/20 hidden sm:block" />
            <div className="flex items-baseline gap-2 text-white">
              <span className="text-accent text-3xl md:text-4xl font-heading font-extrabold">24/7</span>
              <span className="text-sm text-white/70 font-body">{t("hero.available")}</span>
            </div>
            <div className="w-px h-8 bg-white/20 hidden sm:block" />
            <div className="flex items-baseline gap-2 text-white">
              <AnimatedCounter value={15} suffix="+" className="text-accent text-3xl md:text-4xl font-heading font-extrabold" />
              <span className="text-sm text-white/70 font-body">{t("hero.vehicles")}</span>
            </div>
            <div className="hidden lg:flex items-center gap-2 ml-2 text-white/70 text-xs font-body">
              <ShieldCheck className="w-4 h-4 text-accent" />
              <span>Service vérifié — assurances premium</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <a
        href="#services"
        aria-label="Scroll"
        className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="scroll-cue" />
      </a>
    </section>
  );
};

export default Hero;
